/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsForRegex": ["^draft"] }] */
import { FormEvent, useReducer, useState } from 'react';

import { produce } from 'immer';

import { twoFaEmailPasswordVerify } from 'actions/other';
import { DISABLE_2FA_FLOW } from 'components/Disable2faFaceAuth/Disable2faFaceAuth.constants';
import { MIXPANEL_EVENT_NAMES, MIXPANEL_SOURCES } from 'constants/mixpanelEventNames';
import { errorHasACode } from 'helpers/error';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { isEmpty, parseErrorMsg, validateEmail as validEmail } from 'helpers/utils';
import { useAppDispatch } from 'storeHooks';

import {
  ActionTypes,
  BACKEND_ERROR_CODES,
  FRONTEND_VERIFICATION_ERRORS,
} from './useVerifyEmailPassword.constants';
import { Actions, AllErrors, BackendErrors, State } from './useVerifyEmailPassword.types';

const reducer = produce((draft: State, action: Actions) => {
  switch (action.type) {
    case ActionTypes.UPDATE_EMAIL:
      draft.email.value = action.payload.value ?? draft.email.value;
      draft.email.error = action.payload.error;
      break;
    case ActionTypes.UPDATE_PASSWORD:
      draft.password.value = action.payload.value ?? draft.password.value;
      draft.password.error = action.payload.error;
      break;
    case ActionTypes.SUBMIT_ERROR:
      draft.email.error = action.payload.emailError;
      draft.password.error = action.payload.passwordError;

      break;
    default:
      break;
  }
});

const initialState: State = {
  email: {
    value: '',
    error: null,
  },
  password: {
    value: '',
    error: null,
  },
};

const validateEmail = (email: string) => {
  let emailError: FRONTEND_VERIFICATION_ERRORS | null = null;

  const isValidEmail = validEmail(email);
  const isEmptyEmail = isEmpty(email);

  if (!isValidEmail) {
    emailError = FRONTEND_VERIFICATION_ERRORS.INVALID_EMAIL_SUBMITTED;
  }
  if (isEmptyEmail) {
    emailError = FRONTEND_VERIFICATION_ERRORS.EMAIL_REQUIRED;
  }

  return emailError;
};

const validatePassword = (password: string) => {
  return isEmpty(password) ? FRONTEND_VERIFICATION_ERRORS.PASSWORD_REQUIRED : null;
};

const useVerifyEmailPassword = ({
  onvVerificationSuccess,
  updateActiveFlow,
}: {
  onvVerificationSuccess: () => void;
  updateActiveFlow: (flow: DISABLE_2FA_FLOW) => void;
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const [buttonLoading, setButtonLoading] = useState(false);

  const appDispatch = useAppDispatch();

  const onEmailChange = (value: string) => {
    const hasError = validateEmail(value) && !isEmpty(value);

    dispatch({
      type: ActionTypes.UPDATE_EMAIL,
      payload: {
        value,
        error: hasError
          ? { code: FRONTEND_VERIFICATION_ERRORS.INVALID_EMAIL, context: undefined }
          : null,
      },
    });
  };

  const onPasswordChange = (value: string) => {
    dispatch({
      type: ActionTypes.UPDATE_PASSWORD,
      payload: { value, error: null },
    });
  };

  const updateErrors = (
    emailError: State['email']['error'],
    passwordError: State['password']['error']
  ) => {
    dispatch({
      type: ActionTypes.SUBMIT_ERROR,
      payload: { emailError, passwordError },
    });
  };

  const onSubmit = (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    const emailErrorCode = validateEmail(state.email.value);
    const passwordErrorCode = validatePassword(state.password.value);

    const emailError: AllErrors | null = emailErrorCode
      ? { code: emailErrorCode, context: undefined }
      : null;
    const passwordError: AllErrors | null = passwordErrorCode
      ? { code: passwordErrorCode, context: undefined }
      : null;

    if (emailError || passwordError) {
      updateErrors(emailError, passwordError);
      return;
    }

    setButtonLoading(true);

    appDispatch(
      twoFaEmailPasswordVerify({
        data: {
          email: state.email.value,
          password: state.password.value,
        },
      })
    )
      .then(() => {
        onvVerificationSuccess();
      })
      .catch(err => {
        const { error } = parseErrorMsg(err) as any;

        const errorHandlers: Record<BACKEND_ERROR_CODES, () => void> = {
          [BACKEND_ERROR_CODES.PENDING_VERIFICATION]: () => {
            updateErrors(error, null);
          },
          [BACKEND_ERROR_CODES.USER_NOT_FOUND]: () => {
            updateErrors(error, null);
          },
          [BACKEND_ERROR_CODES.DELETED_ACCOUNT_EXISTS]: () => {
            updateErrors(error, null);
          },
          [BACKEND_ERROR_CODES.KYC_NOT_APPROVED]: () => {
            updateActiveFlow(DISABLE_2FA_FLOW.RAISE_TICKET);

            trackMixpanelEvent(
              MIXPANEL_EVENT_NAMES.DISABLE_2FA_WITH_FACE_AUTH.EMAIL_VERIFIICATION_SUCCESS,
              {
                type: 'ticket',
                source: MIXPANEL_SOURCES.LOGIN_PAGE,
                verification_type: 'email',
              }
            );
          },
          [BACKEND_ERROR_CODES.BCRYPT_HASH_LIMIT]: () => {
            updateErrors(null, error);
          },
          [BACKEND_ERROR_CODES.MFA_ALREADY_DISABLED]: () => {
            updateErrors(null, error);
          },

          [BACKEND_ERROR_CODES.INVALID_PASSWORD]: () => {
            updateErrors(null, error);
          },
          [BACKEND_ERROR_CODES.PASSWORD_NOT_SET]: () => {
            updateErrors(null, error);
          },
        };

        if (errorHasACode<BackendErrors>(error)) {
          if (error.code in errorHandlers) {
            errorHandlers[error.code]();
            return;
          }

          updateErrors(null, error);
          return;
        }

        updateErrors(null, {
          code: FRONTEND_VERIFICATION_ERRORS.SOMETHING_WENT_WRONG,
          context: undefined,
        });
      })
      .finally(() => {
        setButtonLoading(false);
      });
  };

  return {
    onEmailChange,
    onSubmit,
    state,
    buttonLoading,
    onPasswordChange,
  };
};

export default useVerifyEmailPassword;
